import { ControllerResponse } from '@/DTOS/Controller/ControllerResponse';
import { RootState } from '@/store';
import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { setDevices } from '../../contextRedux/DeviceType/DeviceTypeStore';
import { resetInfoDevice, updateProps } from '../../contextRedux/InfoDevice/InfoDeviceModel';
import { setInfoDevices } from '../../contextRedux/InfoDevice/InfoDeviceStore';
import DeviceTypeService from '../../services/deviceType/DeviceTypeService';
import InfoDeviceService from '../../services/infoDevice/InfoDeviceService';
import InfoDeviceModal from './InfoDeviceModal/InfoDeviceModal';
import InfoDeviceTable from './InfoDeviceTable/InfoDeviceTable';
import { AnimateButton } from '../../customComponents/button/CustomButton';

interface InfoDeviceProps {}

const InfoDevice: React.FC<InfoDeviceProps> = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.infoDevice.props.searchTerm);
  const skip = useSelector((state: RootState) => state.models?.infoDevice.props.skip);
  const take = useSelector((state: RootState) => state.models?.infoDevice.props.take);
  const refresh = useSelector((state: RootState) => state.models?.infoDevice.props.refresh);

  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));

    previousParamsRef.current = { term: termInUrl, page: pageInUrl.toString(), pageSize: pageSizeInUrl.toString() };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
        };
      }
    }
  }, [searchTerm, skip, take, isParamsInitialized, navigate, location.pathname, location.search]);

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: () =>
      DeviceTypeService.search({
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _dates,
    isFetching: isInfoDevicesLoading,
    isError: isErrorOnFetchInfoDevices,
    error: infoDevicesError,
    refetch,
  } = useQuery({
    queryKey: ['infoDevices', searchTerm, skip, take, refresh],
    queryFn: () =>
      InfoDeviceService.search({
        isDeviceTypeIncluded: true,
        termen: searchTerm || undefined,
        skip,
        take,
      }).then((data: ControllerResponse<any>) => {
        dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
        dispatch(setInfoDevices(data?.response));
        return data?.response;
      }),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  const handleReset = () => {
    dispatch(
      resetInfoDevice({
        isModalVisible: true,
        refresh: true,
        searchTerm,
        skip,
        take,
      })
    );
  };

  if (isDeviceTypesLoading) {
    return <Spin size='large' className='spinner'></Spin>;
  }

  if (isInfoDevicesLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchInfoDevices) return <Alert message={infoDevicesError.message} type='error' />;

  return (
    <>
      <h1 style={{marginBottom:50}}>Informatii Device</h1>
      <AnimateButton onClick={() => handleReset()}>
        Adauga
      </AnimateButton>
      <InfoDeviceModal />
      <InfoDeviceTable />
    </>
  );
};

export default InfoDevice;
